import b from 'b_';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { QuizEditorAnswer, AnswerCardColors } from '../__answer/quiz-editor__answer';
import { QuizEditorInput } from '../__input/quiz-editor__input';
import { actions } from '../actions';
import {
    getCurrentQuestionAnswers,
    getCurrentQuestionIndex,
    getCurrentQuestionTitle,
    getQuestionMaxLength,
    isReadOnly,
} from '../selectors';

import './quiz-editor__question.scss';

const colorsArr: AnswerCardColors[] = ['blue', 'aqua', 'red', 'yellow'];

const INPUT_MAX_HEIGHT = 108;

export const QuizEditorQuestion = ({ showPreviewAnswers }: { showPreviewAnswers?: boolean }) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const questionTitlePlaceholder = intl.formatMessage({ id: 'teacherLab.quizEditor.questionTitlePlaceholder' });

    const readOnly = useSelector(isReadOnly);
    const currentQuestionIndex = useSelector(getCurrentQuestionIndex);
    const currentQuestionTitle = useSelector(getCurrentQuestionTitle);
    const currentQuestionAnswers = useSelector(getCurrentQuestionAnswers);
    const questionMaxLength = useSelector(getQuestionMaxLength);

    const [questionTitleValue, setQuestionTitleValue] = useState(currentQuestionTitle);
    const questionTitleInputRef = useRef<HTMLTextAreaElement>(null);

    const deferredSavingTimer = useRef(0);

    useEffect(() => {
        deferredSavingTimer.current = window.setTimeout(() => {
            dispatch(
                actions.setQuestionTitle({
                    questionIndex: currentQuestionIndex,
                    value: questionTitleValue,
                })
            );
        }, 400);

        return () => {
            window.clearTimeout(deferredSavingTimer.current);
        };
    }, [currentQuestionIndex, dispatch, questionTitleValue]);

    useEffect(() => {
        setQuestionTitleValue(currentQuestionTitle);
    }, [currentQuestionTitle]);

    const handleCheckboxClick = (answerIndex: number) => {
        const currentAnswer = currentQuestionAnswers[answerIndex];
        const status = !currentAnswer.correct;
        dispatch(actions.setAnswerStatus({ questionIndex: currentQuestionIndex, answerIndex, status }));
    };

    const handleAnswerInputBlur = (answerIndex: number, value: string) => {
        dispatch(actions.setAnswerValue({ questionIndex: currentQuestionIndex, answerIndex, value }));
    };

    const handleTitleBlur = () => {
        dispatch(actions.setQuestionTitle({ questionIndex: currentQuestionIndex, value: questionTitleValue }));
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setQuestionTitleValue(event.target.value);
    };

    const handleTitleClick = () => {
        if (questionTitleInputRef.current) {
            questionTitleInputRef.current.focus();
        }
    };

    return (
        <div className="quiz-editor__question">
            <div className="question__title" {...accessibleClick(handleTitleClick)}>
                <QuizEditorInput
                    counterClassName="question__symbols-counter"
                    iconClassName="question__edit-icon"
                    inputRef={questionTitleInputRef}
                    maxHeight={INPUT_MAX_HEIGHT}
                    maxLength={questionMaxLength}
                    onBlur={handleTitleBlur}
                    onChange={handleTitleChange}
                    placeholder={questionTitlePlaceholder}
                    readOnly={readOnly}
                    textareaClassname={b('question__title-input', { 'read-only': readOnly })}
                    value={questionTitleValue}
                />
            </div>
            <div
                className={`question__answers-container question__answers-container_preview-answers_${
                    showPreviewAnswers ? 'yes' : 'no'
                }`}
            >
                {currentQuestionAnswers.map((answer, index) => (
                    <QuizEditorAnswer
                        answerIndex={index}
                        color={colorsArr[index]}
                        isCorrect={answer.correct}
                        key={`${currentQuestionIndex} ${index}`}
                        onAnswerInputBlur={handleAnswerInputBlur}
                        onCheckboxClick={handleCheckboxClick}
                        questionIndex={currentQuestionIndex}
                        readOnly={readOnly}
                        value={answer.value}
                    />
                ))}
            </div>
        </div>
    );
};
